/**
 * useShareGroupService — 共享组业务服务 Hook
 *
 * 消费者层 Hook，封装：
 * 1. API CRUD (fetch / create / update / delete / assign / remove)
 * 2. ShareGroupModal 的可见状态 + 编辑/创建模式
 * 3. 高亮状态管理（highlightedGroupId → highlightedLinkIds）
 * 4. 动态二级菜单构建（buildShareMenuItems）
 * 5. 右键菜单 action 路由（handleShareAction）
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '../../services/api';
import type { ShareGroup, ShareGroupMember } from './types';
import type { GanttTask } from '../wxb-ui/GanttChart/types';
import type { ContextMenuItem } from '../wxb-ui/GanttChart/GanttContextMenu';
import { parseScheduleIdFromTaskId, uniqueScheduleIdsFromTaskIds } from './taskIds';

const API = '/share-groups';

export type ShareMode = 'SAME_TEAM' | 'DIFFERENT';

export interface ModalState {
  visible: boolean;
  mode: 'create' | 'edit';
  editingGroup: ShareGroup | null;
  /** Pre-selected schedule IDs when creating from multi-selection */
  preSelectedIds: number[];
}

export interface UseShareGroupServiceProps {
  templateId: number;
  /** Operation list for Modal dual-column selector */
  operations: Array<{
    scheduleId: number;
    operationName: string;
    operationCode?: string;
    stageName: string;
    operationDay?: number;
    recommendedTime?: number;
    requiredPeople: number;
  }>;
  /** Callback after data changes (used to refresh gantt chart data) */
  onDataChange?: () => void;
  /** Toast callback */
  onMessage?: (type: 'success' | 'warning' | 'error', text: string) => void;
}

export interface UseShareGroupServiceResult {
  // === Data ===
  shareGroups: ShareGroup[];
  loading: boolean;
  /** Currently highlighted group ID (for drawLinks emphasis) */
  highlightedGroupId: number | null;
  /** Derived highlighted link IDs for WxbGanttChart prop */
  highlightedLinkIds: string[];

  // === Modal State ===
  modalState: ModalState;
  openCreateModal: (preSelectedIds?: number[]) => void;
  openEditModal: (group: ShareGroup) => void;
  closeModal: () => void;
  submitModal: (
    mode: ShareMode,
    memberIds: number[]
  ) => Promise<void>;

  // === Context Menu Actions ===
  /** Build dynamic sub-menu items for a given task */
  buildShareMenuItems: (task: GanttTask) => ContextMenuItem[];
  /** Handle a share-* menu action key */
  handleShareAction: (
    actionKey: string,
    taskOrIds: GanttTask | string[]
  ) => Promise<void>;

  // === Highlight ===
  toggleHighlight: (groupId: number) => void;
  clearHighlight: () => void;

  // === Refresh ===
  refresh: () => Promise<void>;
}

const defaultMessage = (type: 'success' | 'warning' | 'error', text: string) => {
  // eslint-disable-next-line no-console
  console.log(`[ShareGroupService] ${type}: ${text}`);
};

const getScheduleIdFromTask = (task: GanttTask): number | null => {
  const explicit = Number(task.data?.scheduleId ?? task.data?.id);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return parseScheduleIdFromTaskId(task.id);
};

export function useShareGroupService({
  templateId,
  operations,
  onDataChange,
  onMessage = defaultMessage,
}: UseShareGroupServiceProps): UseShareGroupServiceResult {
  // ===== State =====
  const [shareGroups, setShareGroups] = useState<ShareGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedGroupId, setHighlightedGroupId] = useState<number | null>(null);

  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    mode: 'create',
    editingGroup: null,
    preSelectedIds: [],
  });

  // ===== Derived: highlightedLinkIds for WxbGanttChart =====
  const highlightedLinkIds = useMemo((): string[] => {
    if (highlightedGroupId === null) return [];
    return [`share_${highlightedGroupId}`];
  }, [highlightedGroupId]);

  // ===== API: Fetch =====
  const fetchGroups = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/template/${templateId}`);
      const normalized: ShareGroup[] = (data || []).map((g: any) => ({
        ...g,
        id: Number(g.id),
        template_id: Number(g.template_id),
        operation_count: g.operation_count !== undefined ? Number(g.operation_count) : undefined,
        members: Array.isArray(g.members)
          ? g.members.map((member: ShareGroupMember) => ({
            ...member,
            id: Number(member.id),
            schedule_id: Number(member.schedule_id),
          }))
          : [],
      }));
      setShareGroups(normalized);
    } catch (error) {
      console.error('Failed to fetch share groups:', error);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ===== Modal Actions =====
  const openCreateModal = useCallback((preSelectedIds?: number[]) => {
    setModalState({
      visible: true,
      mode: 'create',
      editingGroup: null,
      preSelectedIds: preSelectedIds || [],
    });
  }, []);

  const openEditModal = useCallback((group: ShareGroup) => {
    setModalState({
      visible: true,
      mode: 'edit',
      editingGroup: group,
      preSelectedIds: group.members?.map((m) => m.schedule_id) || [],
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, visible: false }));
  }, []);

  const submitModal = useCallback(
    async (mode: ShareMode, memberIds: number[]) => {
      if (memberIds.length < 2) {
        onMessage('warning', '共享组至少需要包含 2 个操作');
        return;
      }

      // Auto-generate group_name from stable operation identity, not name alone.
      const names = memberIds.slice(0, 2)
        .map((sid) => {
          const operation = operations.find((item) => item.scheduleId === sid);
          if (!operation) return null;
          return operation.operationCode
            ? `${operation.operationCode} ${operation.operationName}`
            : `${operation.operationName} #${operation.scheduleId}`;
        })
        .filter(Boolean);
      const autoName = names.join(' + ') + (memberIds.length > 2 ? ` +${memberIds.length - 2}` : '') || '共享组';

      try {
        const payload = {
          group_name: autoName,
          share_mode: mode,
          member_ids: memberIds,
        };

        if (modalState.mode === 'edit' && modalState.editingGroup) {
          await api.put(`${API}/${modalState.editingGroup.id}`, payload);
          onMessage('success', '共享组已更新');
        } else {
          await api.post(`${API}/template/${templateId}`, payload);
          onMessage('success', '共享组已创建');
        }

        closeModal();
        await fetchGroups();
        onDataChange?.();
      } catch (error: any) {
        const msg = error?.response?.data?.error || '保存失败';
        onMessage('error', msg);
      }
    },
    [modalState, templateId, operations, closeModal, fetchGroups, onDataChange, onMessage]
  );

  // ===== Single-item API Actions =====
  const assignToGroup = useCallback(
    async (scheduleId: number, groupId: number) => {
      try {
        await api.post(`${API}/assign`, {
          schedule_id: scheduleId,
          share_group_id: groupId,
        });
        return true;
      } catch (error: any) {
        // 409 = already in group (tolerable)
        if (error?.response?.status === 409) return false;
        throw error;
      }
    },
    []
  );

  // ===== Highlight =====
  const toggleHighlight = useCallback(
    (groupId: number) => {
      setHighlightedGroupId((prev) => (prev === groupId ? null : groupId));
    },
    []
  );

  const clearHighlight = useCallback(() => {
    setHighlightedGroupId(null);
  }, []);

  // ===== Build Dynamic Context Menu =====
  const buildShareMenuItems = useCallback(
    (task: GanttTask): ContextMenuItem[] => {
      const scheduleId = getScheduleIdFromTask(task);
      const items: ContextMenuItem[] = [];

      // 1. New share group
      items.push({ key: 'share-new', label: '新建共享组' });

      // 2. Always offer the server-authoritative all-groups disconnect action.
      // The local group list is display-only: a stale/incomplete cache must never
      // downgrade the command to a partial or unavailable disconnect.
      const myGroups = shareGroups.filter(g =>
        g.members?.some(m => Number(m.schedule_id) === scheduleId)
      );
      if (scheduleId !== null) {
        items.push({ key: 'share-divider-unlink', label: '—', divider: true });
        items.push({
          key: 'share-unlink',
          label: myGroups.length > 0
            ? `断开全部共享组 (${myGroups.length})`
            : '断开全部共享组',
          danger: true,
        });
      }

      return items;
    },
    [shareGroups]
  );

  // ===== Handle Menu Actions =====
  const handleShareAction = useCallback(
    async (actionKey: string, taskOrIds: GanttTask | string[]) => {
      // Resolve scheduleIds depending on input type
      const resolveScheduleIds = (): number[] => {
        if (Array.isArray(taskOrIds)) {
          // Multi-select IDs may include equipment mirror suffixes, e.g.
          // operation_123__equip-45. Resolve them to schedule IDs and de-dupe.
          return uniqueScheduleIdsFromTaskIds(taskOrIds);
        }
        // Single task
        const task = taskOrIds as GanttTask;
        const scheduleId = getScheduleIdFromTask(task);
        return scheduleId === null ? [] : [scheduleId];
      };

      // share-new → open create modal
      if (actionKey === 'share-new') {
        const ids = resolveScheduleIds();
        openCreateModal(ids);
        return;
      }

      // share-join-{groupId} → assign task(s) to group
      if (actionKey.startsWith('share-join-')) {
        const groupId = parseInt(actionKey.replace('share-join-', ''), 10);
        if (!Number.isFinite(groupId)) return;

        const scheduleIds = resolveScheduleIds();
        let added = 0;
        let skipped = 0;

        try {
          for (const sid of scheduleIds) {
            const ok = await assignToGroup(sid, groupId);
            if (ok) added++;
            else skipped++;
          }

          if (skipped > 0 && added > 0) {
            onMessage('success', `已加入 ${added} 个（${skipped} 个已在组中）`);
          } else if (skipped > 0) {
            onMessage('warning', '所选操作已在该组中');
          } else {
            onMessage('success', '已加入共享组');
          }

          await fetchGroups();
          onDataChange?.();
        } catch {
          onMessage('error', '加入共享组失败');
        }
        return;
      }

      // share-highlight-{groupId} → toggle highlight
      if (actionKey.startsWith('share-highlight-')) {
        const groupId = parseInt(actionKey.replace('share-highlight-', ''), 10);
        if (Number.isFinite(groupId)) {
          toggleHighlight(groupId);
        }
        return;
      }

      // share-unlink → remove task from ALL groups
      if (actionKey === 'share-unlink') {
        const scheduleIds = resolveScheduleIds();
        if (scheduleIds.length === 0) {
          onMessage('error', '未找到可断开的模板工序');
          return;
        }
        try {
          // Each endpoint also removes a now-degenerate group. Running two
          // members of the same group concurrently can deadlock when both
          // transactions attempt to delete that group. Serialize the small
          // de-duplicated set; every call is idempotent and server-authoritative.
          const responses: Array<{ data?: { removed_memberships?: number } }> = [];
          for (const sid of scheduleIds) {
            responses.push(await api.delete(`${API}/operation/${sid}/groups`));
          }
          const removedMemberships = responses.reduce(
            (total, response) => total + Number(response.data?.removed_memberships || 0),
            0
          );
          if (removedMemberships > 0) {
            onMessage('success', `已断开全部共享组（${removedMemberships} 个成员关系）`);
          } else {
            onMessage('warning', '该操作没有共享组关系');
          }
          await fetchGroups();
          onDataChange?.();
        } catch {
          onMessage('error', '断开全部共享组失败');
        }
        return;
      }
    },
    [
      openCreateModal,
      assignToGroup,
      toggleHighlight,
      fetchGroups,
      onDataChange,
      onMessage,
    ]
  );

  // ===== Refresh =====
  const refresh = useCallback(async () => {
    await fetchGroups();
  }, [fetchGroups]);

  return {
    shareGroups,
    loading,
    highlightedGroupId,
    highlightedLinkIds,
    modalState,
    openCreateModal,
    openEditModal,
    closeModal,
    submitModal,
    buildShareMenuItems,
    handleShareAction,
    toggleHighlight,
    clearHighlight,
    refresh,
  };
}
